import { getBitValueFromBool, Logger } from '../../../common/utils';
import {
	ATLASSIAN_CONSENT_COOKIE_KEY,
	ATLASSIAN_CONSENT_FALLBACK_COOKIE_KEY,
	ATLASSIAN_SHOW_BANNER_COOKIE_KEY,
} from '../../../constants';
import { getBannerVisibility } from '../../banner-controls/get-banner-visibility';
import { setStrictlyNecessaryCookie } from '../set-strictly-necessary-cookie';

const MIN_RANDOMIZED_CONSENT_COOKIE_LIFETIME_MINUTES = 50;
const MAX_RANDOMIZED_CONSENT_COOKIE_LIFETIME_MINUTES = 70;

export const getRandomizedConsentCookieLifetimeMinutes = (): number => {
	// Random offset is 0-20 minutes inclusive, so the final lifetime spreads across 50-70 minutes.
	const randomizedOffsetMinutes = Math.floor(
		Math.random() *
			(MAX_RANDOMIZED_CONSENT_COOKIE_LIFETIME_MINUTES -
				MIN_RANDOMIZED_CONSENT_COOKIE_LIFETIME_MINUTES +
				1),
	);

	// Spread cookie expiration times to smooth traffic spikes to ConsentHub.
	return MIN_RANDOMIZED_CONSENT_COOKIE_LIFETIME_MINUTES + randomizedOffsetMinutes;
};

// Pick once per module load so both required consent cookies share the same spread-out expiry.
const RANDOMIZED_CONSENT_COOKIE_LIFETIME_MINUTES = getRandomizedConsentCookieLifetimeMinutes();

const getRequiredConsentCookieExpiryDate = (): Date => {
	const expiryDate = new Date();

	expiryDate.setTime(expiryDate.getTime() + RANDOMIZED_CONSENT_COOKIE_LIFETIME_MINUTES * 60 * 1000);
	return expiryDate;
};

export const setConsentTokenCookie = (consentToken: string): void => {
	setStrictlyNecessaryCookie(ATLASSIAN_CONSENT_COOKIE_KEY, consentToken, {
		expires: getRequiredConsentCookieExpiryDate(),
		path: '/',
	});
	Logger.debug(`Set Consent Token ${ATLASSIAN_CONSENT_COOKIE_KEY} ${consentToken}`);
};

export const setConsentTokenFallbackCookie = (consentToken: string): void => {
	const inOneYear = new Date();
	inOneYear.setFullYear(inOneYear.getFullYear() + 1);
	setStrictlyNecessaryCookie(ATLASSIAN_CONSENT_FALLBACK_COOKIE_KEY, consentToken, {
		expires: inOneYear,
		path: '/',
	});
	Logger.debug(
		`set fallback consent token: ${ATLASSIAN_CONSENT_FALLBACK_COOKIE_KEY} ${consentToken}`,
	);
};

export const setShouldShowBannerCookie = (
	consentToken: string,
	consentTimestamp?: string,
): void => {
	// Since we have no good way to reference this as a global state object, we want to precompute and store the
	// banner visibility in a temporary, session-scope cookie (same expiry as the other cookies), which we can
	// lookup in the UI as needed
	const bannerVisibility = getBannerVisibility({
		consentToken,
		consentTimestamp,
	});
	const shouldShowBanner = getBitValueFromBool(!!bannerVisibility);

	setStrictlyNecessaryCookie(ATLASSIAN_SHOW_BANNER_COOKIE_KEY, shouldShowBanner, {
		expires: getRequiredConsentCookieExpiryDate(),
		path: '/',
	});
	Logger.debug(
		`set shouldShowBannerCookie: ${ATLASSIAN_SHOW_BANNER_COOKIE_KEY} ${shouldShowBanner}`,
	);
};

export const setRequiredConsentCookies = (
	consentToken: string,
	consentTimestamps?: string,
): void => {
	setConsentTokenCookie(consentToken);
	setShouldShowBannerCookie(consentToken, consentTimestamps);
};
